/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorPlugins } from '../../types/EditorPlugins';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import type { EditorType } from '@ckeditor/ckeditor5-metadata/src';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import { requiresMinimapContainer } from '../requiresMinimapContainer/requiresMinimapContainer';
import { requiresCommentsContainer } from '../requiresCommentsContainer/requiresCommentsContainer';
import { requiresDocumentOutlineContainer } from '../requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { requiresPresenceListContainer } from '../requiresPresenceListContainer/requiresPresenceListContainer';
import {
	requiresRevisionHistoryContainers
} from '../requiresRevisionHistoryContainers/requiresRevisionHistoryContainers';
import {
	EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER,
	EDITOR_CONTAINER_IDENTIFIER,
	EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER,
	EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER,
	EDITOR_MINIMAP_SIDEBAR_IDENTIFIER,
	EDITOR_OUTLINE_SIDEBAR_IDENTIFIER,
	EDITOR_PRESENCE_LIST_IDENTIFIER,
	EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER,
	EDITOR_REVISION_HISTORY_IDENTIFIER,
	EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER,
	EDITOR_WORD_COUNT
} from '../../constants/identifiers';
import { getAngularRef } from '../getAngularRef/getAngularRef';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';

export function getViewChildRefs( editorPlugins: EditorPlugins, toolbarOptions: ToolbarOptions, editorType: EditorType ): string {
	const isDecoupled = isDecoupledEditor( editorType );
	const hasMinimap = requiresMinimapContainer( editorPlugins );
	const hasAnnotations = requiresCommentsContainer( editorPlugins );
	const hasDocumentOutline = requiresDocumentOutlineContainer( editorPlugins );
	const hasPresenceList = requiresPresenceListContainer( editorPlugins );
	const hasRevisionHistory = requiresRevisionHistoryContainers( editorPlugins );
	const hasMenuBar = toolbarOptions.isMenuBarUsed;
	const hasWordCount = requiresWordCountPlugin( editorPlugins );

	return [
		isDecoupled ? getViewChild( EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER ) : '',
		hasMenuBar ? getViewChild( EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER ) : '',
		hasMinimap ? getViewChild( EDITOR_MINIMAP_SIDEBAR_IDENTIFIER ) : '',
		hasAnnotations ? getViewChild( EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER ) : '',
		hasPresenceList ? getViewChild( EDITOR_PRESENCE_LIST_IDENTIFIER ) : '',
		hasDocumentOutline ? getViewChild( EDITOR_OUTLINE_SIDEBAR_IDENTIFIER ) : '',
		hasRevisionHistory ? getViewChild( EDITOR_CONTAINER_IDENTIFIER ) : '',
		hasRevisionHistory ? getViewChild( EDITOR_REVISION_HISTORY_IDENTIFIER ) : '',
		hasRevisionHistory ? getViewChild( EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER ) : '',
		hasRevisionHistory ? getViewChild( EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER ) : '',
		hasWordCount ? getViewChild( EDITOR_WORD_COUNT ) : ''
	].filter( Boolean ).join( '\n' );
}

function getViewChild( selector: string ) {
	const { htmlRefElement, variableName } = getAngularRef( selector );

	return `@ViewChild( '${ htmlRefElement }' ) private ${ variableName }!: ElementRef<HTMLDivElement>;`;
}
